import { Check, ChevronsUpDown, Combine, Layers } from 'lucide-react'
import { useState } from 'react'
import { Link, useLocation, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { StatusIcon } from '@/components/sv/status-icon'
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany, useCompanyPath } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  AppSwitcherQuery,
  AppSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/app/app-switcher/switcher.types.gen'
import type { AppStatus } from '@/graphql.types'

const APP_SWITCHER_QUERY = gql`
  query AppSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      apps {
        id
        total
        items {
          id
          status
          name
          displayName
          pipelineStage {
            id
            pipeline {
              id
              displayName
            }
          }
        }
      }
    }
  }
`

export const AppSwitcherData = () => {
  const { pathname } = useLocation()
  const { getCompanyPath } = useCompanyPath()
  const [open, setOpen] = useState(false)
  const { appName = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    AppSwitcherQuery,
    AppSwitcherQueryVariables
  >(APP_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const apps = data?.company?.apps?.items ?? []

  const activeApp = apps.find((app) => app.name === appName) || apps[0]
  const pipeline = activeApp?.pipelineStage?.pipeline
  const pipelineDisplayName = pipeline?.displayName ?? ''
  const idPipeline = pipeline?.id ?? ''

  return (
    <>
      {pipelineDisplayName ? (
        <>
          <Link
            to={getCompanyPath({
              pathname: '/pipeline/:idPipeline',
              variables: { idPipeline },
            })}
          >
            <BreadcrumbItem>
              <BreadcrumbPage className="cursor-pointer p-2 flex items-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground">
                <BreadcrumbLink asChild>
                  <div className="flex items-center gap-2">
                    <Combine className="size-3.5" />
                    <span className="max-w-[200px] truncate">
                      {pipelineDisplayName}
                    </span>
                  </div>
                </BreadcrumbLink>
              </BreadcrumbPage>
            </BreadcrumbItem>
          </Link>
          <BreadcrumbSeparator />
        </>
      ) : null}
      <BreadcrumbItem>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <SidebarMenuButton
              size="lg"
              aria-expanded={open}
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="grid flex-1 text-left text-sm leading-tight">
                <div className="flex items-center gap-2">
                  <StatusIcon status={activeApp?.status as AppStatus} />
                  <span
                    title={activeApp?.displayName ?? ''}
                    className="max-w-[250px] truncate text-foreground font-medium"
                  >
                    {activeApp?.displayName ?? ''}
                  </span>
                </div>
              </div>
              <ChevronsUpDown className="ml-auto" />
            </SidebarMenuButton>
          </PopoverTrigger>
          <PopoverContent
            className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
            align="start"
            side="bottom"
            sideOffset={4}
          >
            <Command>
              <CommandInput
                placeholder="Search application..."
                className="h-9"
              />
              <CommandList>
                <CommandEmpty>No application found</CommandEmpty>
                <CommandGroup>
                  {apps.map((app) => (
                    <Link
                      key={app.id}
                      to={`${pathname.replace(appName, app.name)}${pathname.endsWith('/') ? '' : '/'}?idCompany=${idCompany}`}
                      onClick={() => setOpen(false)}
                    >
                      <CommandItem
                        value={app.id}
                        keywords={[app.displayName]}
                        className="cursor-pointer"
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border">
                          {activeApp?.id === app.id ? (
                            <Check className="size-3.5 shrink-0" />
                          ) : (
                            <Layers className="size-3.5 shrink-0" />
                          )}
                        </div>
                        <span
                          className={cn('truncate', {
                            'font-medium': activeApp?.id === app?.id,
                          })}
                        >
                          {app.displayName}
                        </span>
                      </CommandItem>
                    </Link>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </BreadcrumbItem>
    </>
  )
}
